import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Star, Loader2, MessageCircle } from 'lucide-react';
import { fetchReviewsForTarget, calculateReviewStats } from '../../services/reviewService';
import ReviewCard from '../../components/reviews/ReviewCard';

export default function InstructorReviews() {
  const { currentUser } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, total: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        const data = await fetchReviewsForTarget(currentUser.uid);
        setReviews(data);
        setStats(calculateReviewStats(data));
      } catch (err) {
        console.error("Error fetching reviews:", err);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) loadReviews();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div>
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">تقييمات الطلاب</h1>
        <p className="text-slate-500 dark:text-zinc-400 font-medium">يمكنك الرد على تقييمات طلابك لتعزيز التفاعل والمصداقية.</p>
      </div>

      {/* Stats Overview */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-10">
          {/* Average Score */}
          <div className="flex flex-col items-center justify-center w-44 shrink-0">
            <div className="text-6xl font-black bg-clip-text text-transparent bg-gradient-to-br from-amber-400 to-amber-600 mb-2">
              {stats.average}
            </div>
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  size={20}
                  className={star <= Math.round(stats.average) ? "fill-amber-400 text-amber-400" : "fill-transparent text-gray-300 dark:text-gray-600"}
                />
              ))}
            </div>
            <p className="text-sm font-bold text-slate-500 dark:text-zinc-400">
              {stats.total} تقييم
            </p>
          </div>

          {/* Bar Distribution */}
          <div className="flex-1 w-full space-y-3">
            {[5, 4, 3, 2, 1].map(stars => {
              const count = stats.distribution[stars] || 0;
              const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
              return (
                <div key={stars} className="flex items-center gap-4">
                  <span className="w-16 font-bold text-slate-600 dark:text-zinc-300 flex items-center gap-1 text-sm">
                    {stars} <Star size={14} className="fill-amber-400 text-amber-400" />
                  </span>
                  <div className="flex-1 h-3 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="w-12 text-sm font-bold text-slate-500 dark:text-zinc-400 text-left">
                    {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="bg-slate-50 dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-16 text-center">
          <MessageCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">لا توجد تقييمات بعد</h3>
          <p className="text-slate-500 dark:text-zinc-400 font-medium text-lg">عندما يقيمك طلابك ستظهر تقييماتهم هنا.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </div>
  );
}
